import React, { Component } from 'react';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { RouteComponentProps } from "react-router-dom";
import { Box, Card, Grid, Typography } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import IbssActionButton, { IActionButton } from '../../../../Components/Buttons/ActionButton/IbssActionButton';
import IbssSvgIcon from '../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import Bin from '../../../../Components/Icons/Bin';
import ServerDataGrid, { DataGridQueryResult, IDataQuery, IPages } from '../../../../Components/Data/ServerDataGrid/ServerDataGrid';
import ConfirmModal from '../../../../Components/Dialogs/ConfirmDialog/ConfirmModal';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import Helper, { getCurrencyByNodeId } from '../../../../Common/Helper';
import { ICateringTemplate, IMenuItem, CateringTemplate, } from './DataModels';
import { rights } from "../../../../Common/RightsManager";
import { PagedResponse } from '../../../../Providers.Api/Models';
import { ICateringMenuExpanded } from '../../../Shared/CateringOrders/Edit/DataModels';
import { QueryParams } from './QueryParams';

class ListCateringTemplates extends IbssPage<IProps, IState, QueryParams>
{
    private get appState() { return appContext().state; }
    private dataGridRef = React.createRef<ServerDataGrid<ICateringTemplate>>();
    private get dataGrid() { return this.dataGridRef.current; }
    private columns: GridColDef<ICateringTemplate>[];
    private get labels() { return appContext().labels; }
    private userCanDelete: boolean;
    private userCanCreate: boolean;

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.userCanDelete = rights.DATAMODEL.CateringTemplates.Delete.has();
        this.userCanCreate = rights.DATAMODEL.CateringTemplates.Create.has();

        this.state = {
            buildingId: this.appState.buildingId,
            isLoading: false,
            showConfirmDialog: false,
            selectedTemplates: [],
            pagesOfTemplates: {
                pages: [],
                pageIndex: 0,
                skipToken: '',
            },
            menuDetails: []
        };

        this.columns = [
            {
                field: Helper.nameOf<ICateringTemplate>("name"),
                headerName: this.labels.funcOrderName_S,
                width: 350,
                disableColumnMenu: true
            },
            {
                field: Helper.nameOf<ICateringTemplate>("menuName"),
                headerName: this.labels.funcMenu_S,
                width: 350,
                disableColumnMenu: true,
                renderCell: (params) => params.row.menuName
            },
            {
                field: Helper.nameOf<ICateringTemplate>("menuMenuItems"),
                headerName: this.labels.funcItem_S,
                width: 350,
                disableColumnMenu: true,
                renderCell: (params) => params.row.menuMenuItems
            },
            {
                field: Helper.nameOf<ICateringTemplate>("costpp"),
                headerName: this.labels.funcCostPP_S,
                width: 350,
                disableColumnMenu: true,
                renderCell: (params) => `${getCurrencyByNodeId(this.state.buildingId)}${params.row.costpp}`
            }
        ];
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcCateringTemplate;
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        const queryParams = this.queryParams;
        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }

        await this.setStateAsync({ buildingId: queryParams.building });
        await this.dataGrid?.refresh();
    }

    private async fetchData(gridQuery: IDataQuery): Promise<{ templates: CateringTemplate[], skipToken: string }>
    {
        const response = await appContext().ibssApiClientV2.v2.byNodeid.cateringTemplates.get<PagedResponse<CateringTemplate[]>>({
            nodeId: this.state.buildingId,
            select: CateringTemplate,
            top: 100,
            skipToken: gridQuery.skipToken,
            filter: "Deleted eq false",
        });

        const menuIds = Array.from(new Set(response.value.map(i => i.Menu_Id)));
        const existingMenuIds = this.state.menuDetails.map(menu => menu.Menu_Id);
        const missingMenuIds = menuIds.filter(id => !existingMenuIds.includes(id));

        if (missingMenuIds.length > 0)
        {
            const menuIdsQuery = missingMenuIds.map(id => `Menu_Id eq '${id}'`).join(' or ');
            const menuDetails = await appContext().ibssApiClientV2.v2.byNodeid.cateringMenu.get<PagedResponse<ICateringMenuExpanded[]>>({
                nodeId: this.state.buildingId,
                select: ICateringMenuExpanded,
                filter: menuIdsQuery
            });

            await this.setStateAsync({
                menuDetails: [...this.state.menuDetails, ...menuDetails.value]
            });
        }

        return { templates: response.value, skipToken: response.skipToken };
    }    
    
    private processTemplateData(template: CateringTemplate): ICateringTemplate
    {
        let menuItems: IMenuItem[] = [];
        try
        {
            if (template.MenuItems && Array.isArray(template.MenuItems))
            {
                menuItems = template.MenuItems as IMenuItem[];
            }
        } catch (error)
        {
        }

        const menu = this.state.menuDetails.find(m => m.Menu_Id === template.Menu_Id);
        const totalCost = menuItems.reduce((sum, item) =>
        {
            const menuItem = menu?.Menu_MenuItems?.find(mi => mi.MenuItem.MenuItem_Id === item.MenuItemId);
            if (menuItem)
            {
                return sum + menuItem.MenuItem.RetailPrice;
            }
            return sum;
        }, 0);

        return {
            cateringTemplateId: template.MenuTemplate_Id,
            menuId: template.Menu_Id,
            description: template.Description,
            deleted: template.Deleted,
            menuItems: menuItems,
            minimumBookingDuration: template.MinimumBookingDuration,
            name: template.Name,
            nodeId: template.Node_Id,
            menuName: menu?.Name,
            menuMenuItems: menuItems.length.toString(),
            costpp: totalCost
        } as ICateringTemplate;
    }

    private async dataQueryChanged(gridQuery: IDataQuery): Promise<DataGridQueryResult<ICateringTemplate>>
    {
        this.setState({ isLoading: true });
        try
        {
            const { templates, skipToken } = await this.fetchData(gridQuery);
            const filteredTemplates = templates.filter(template => !template.Deleted);
            const rows = filteredTemplates.map(template => this.processTemplateData(template));

            this.setState({ isLoading: false });
            return new DataGridQueryResult(rows, skipToken);
        } 
        catch (error)
        {
            this.setState({ isLoading: false });
            return new DataGridQueryResult([], "");
        }
    }

    private handleSelectionChange(selection: GridRowSelectionModel): void
    {
        const pageOfRows = this.dataGrid?.rows as ICateringTemplate[];
        const selectedRows = pageOfRows.filter(row => selection.includes(row.cateringTemplateId));
        this.setState({ selectedTemplates: selectedRows });
    }

    private showConfirmDeletionModal(): void
    {
        this.setState({ showConfirmDialog: true });
    }

    private async deleteTemplates(): Promise<void>
    {
        this.setState({ isLoading: true });
        try
        {
            await Promise.all(
                this.state.selectedTemplates.map(template =>
                    appContext().ibssApiClientV2.v2.byNodeid.cateringTemplates.byId.delete({
                        nodeId: template.nodeId,
                        id: template.cateringTemplateId,
                    })
                )
            );
            await this.dataGrid?.refresh();
        }
        finally
        {
            this.setState({ isLoading: false, showConfirmDialog: false, selectedTemplates: []});
        }
    }

    private handleRowClick = (params: any) =>
    {
        const cateringTemplateId = params.row.cateringTemplateId;
        this.props.history.push(`/oneLens/create-catering-template/${this.state.buildingId}/${cateringTemplateId}/edit`);
    };

    public get actionButtons(): IActionButton[]
    {
        return [
            {
                label: this.labels.HubButtonDelete,
                icon: (
                    <IbssSvgIcon fontSize='small' className="mr-1">
                        <Bin />
                    </IbssSvgIcon>
                ),
                color: "error",
                onClick: () => this.showConfirmDeletionModal(),
                disabled: !this.state.selectedTemplates.length || !this.userCanDelete,
            },
        ];
    }

    public render(): JSX.Element
    {
        return (
            <div className="page-height-exct-header">
                <div className="rightPanel-main-content">
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Typography variant="h5" component="h1" gutterBottom>
                                    {this.labels.funcCateringTemplate_L}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {this.labels.funcCateringTemplate_D}
                                </Typography>
                            </Box>
                            <IbssButton
                                variant="contained"
                                color="primary"
                                disabled={!this.userCanCreate}
                                onClick={() => this.props.history.push(`/oneLens/create-catering-template/${this.state.buildingId}/edit`)}                            >
                                {this.labels.funcCreateNewTemplate}
                            </IbssButton>
                        </Box>
                    </Card>
                    <div className="table-panel">
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1, mr: 0 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Typography variant="h6" color="text.secondary">
                                                {this.labels.funcAllOrders}
                                            </Typography>
                                        </Box>
                                        <IbssActionButton buttons={this.actionButtons} />
                                    </Box>
                                    <Box>
                                        <ServerDataGrid
                                            loading={this.state.isLoading}
                                            className="data-grid-clickable-row"
                                            ref={this.dataGridRef}
                                            loadDataOnMount={false}
                                            checkboxSelection
                                            height="calc(-328px + 100vh)"
                                            columns={this.columns}
                                            disableRowSelectionOnClick
                                            pageSizeOptions={[100]}
                                            pages={this.state.pagesOfTemplates}
                                            onDataQueryChange={i => this.dataQueryChanged(i)}
                                            onPagesChange={pages => this.setStateAsync({ pagesOfTemplates: pages })}
                                            onRowSelectionModelChange={e => this.handleSelectionChange(e)}
                                            getRowId={(row) => row.cateringTemplateId}
                                            onRowClick={(params) => this.handleRowClick(params)}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <ConfirmModal
                            show={this.state.showConfirmDialog}
                            modalHeading={this.labels.HubLabelModalConfirmTitle}
                            modalMessage={this.labels.funcCateringTemplateConfirmDelete_Message}
                            handleModal={() => this.setState({ showConfirmDialog: false })}
                            okButton={() => this.deleteTemplates()}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default ListCateringTemplates;


export interface IProps extends RouteComponentProps
{
}

export interface IState
{
    buildingId: number;
    isLoading: boolean;
    showConfirmDialog: boolean;
    selectedTemplates: ICateringTemplate[];
    pagesOfTemplates: IPages<ICateringTemplate>;
    menuDetails: ICateringMenuExpanded[];
}